var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useMemo, useState, useEffect, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { useUploadedAssets } from './hooks/useUploadedAssets';
import { fetchFolderIdForShopper } from './utilities/folderUtilities';
import { showValidAssets } from './utilities/assetFilters';
import { isWindowDefined } from './utilities/isWindowDefined';
export var UploadingAnnouncementKeys;
(function (UploadingAnnouncementKeys) {
    UploadingAnnouncementKeys["UploadingStart"] = "UploadingStart";
    UploadingAnnouncementKeys["UploadingFinished"] = "UploadingFinished";
    UploadingAnnouncementKeys["UploadingError"] = "UploadingError";
})(UploadingAnnouncementKeys || (UploadingAnnouncementKeys = {}));
const UploadManagerContext = createContext(undefined);
/**
 * Provider context for the UploadManagerProvider
 */
export const useUploadManager = () => {
    const result = useContext(UploadManagerContext);
    if (!result) {
        throw new Error('Should be used inside an UploadManagerProvider.');
    }
    return result;
};
function resolveAssetStore(subBrand, experience, writeTenant, assetStoreOverride, authProvider) {
    return __awaiter(this, void 0, void 0, function* () {
        const isBrowser = isWindowDefined();
        if (assetStoreOverride) {
            return assetStoreOverride;
        }
        if (isBrowser && authProvider) {
            const { VistaAssetStore } = yield import('@design-stack-vista/vista-assets-sdk');
            return new VistaAssetStore({
                authProvider,
                subBrand,
                experience,
                writeTenant,
            });
        }
        return undefined;
    });
}
/**
 * Provider whose context provides the assetStore, whether or not there are any assets, when the assets expire on, and callbacks for onError and onUpload
 */
export const UploadManagerProvider = observer((props) => {
    const { authProvider, subBrand, experience, writeTenant, assetsExpireOn = 'never', onUpload, onUploadCompleted, onAssetDeleted, onError, children, fetchLimit, folderName, assetStore: assetStoreOverride, brandKitLogoAssetIds, } = props;
    const [folderIdForShopper, setFolderIdForShopper] = useState();
    const [visibleAssets, setVisibleAssets] = useState([]);
    const [visibleLogoAssets, setVisibleLogoAssets] = useState([]);
    const [assetStore, setAssetStore] = useState(assetStoreOverride);
    const [uploadingAnnouncement, setUploadingAnnouncement] = useState(undefined);
    useEffect(() => {
        if (!assetStore) {
            resolveAssetStore(subBrand, experience, writeTenant, assetStoreOverride, authProvider).then(setAssetStore);
        }
    }, [authProvider, experience, assetStore, subBrand, writeTenant, assetStoreOverride]);
    const uploadedAssets = useUploadedAssets({ assetStore, onError, fetchLimit, folderName });
    const fetchLinkedAssets = useCallback((linkedAssetIds) => __awaiter(void 0, void 0, void 0, function* () {
        if (!linkedAssetIds.length || !assetStore)
            return [];
        try {
            const linkedAssets = yield Promise.all(linkedAssetIds.map((id) => __awaiter(void 0, void 0, void 0, function* () {
                try {
                    const asset = yield assetStore.fetchSingleAsset({ id });
                    if (!asset) {
                        console.error(`Asset with ID ${id} not found.`);
                        return null;
                    }
                    yield asset.presign();
                    return asset;
                }
                catch (error) {
                    console.error(`Error processing asset with ID ${id}:`, error);
                    return null;
                }
            })));
            return linkedAssets.filter((asset) => asset !== null);
        }
        catch (error) {
            console.error('Error fetching linked assets:', error);
            return [];
        }
    }), [assetStore]);
    useEffect(() => {
        if (uploadedAssets.status !== 'ready' || !brandKitLogoAssetIds || brandKitLogoAssetIds.length === 0) {
            return;
        }
        const assetMap = new Map(assetStore === null || assetStore === void 0 ? void 0 : assetStore.assets.map((asset) => { var _a; return [(_a = asset.data) === null || _a === void 0 ? void 0 : _a.id, asset]; }));
        const userLogoAssets = [];
        const linkedAssetIds = [];
        brandKitLogoAssetIds.forEach((id) => {
            const userAsset = assetMap.get(id);
            if (userAsset) {
                userLogoAssets.push(userAsset);
            }
            else {
                linkedAssetIds.push(id);
            }
        });
        const fetchAndSetLinkedAssets = () => __awaiter(void 0, void 0, void 0, function* () {
            const linkedAssets = yield fetchLinkedAssets(linkedAssetIds);
            userLogoAssets.push(...linkedAssets);
            setVisibleLogoAssets(userLogoAssets);
        });
        fetchAndSetLinkedAssets();
    }, [brandKitLogoAssetIds, assetStore, uploadedAssets.status, fetchLinkedAssets]);
    const hasAssets = Boolean(assetStore === null || assetStore === void 0 ? void 0 : assetStore.assets.some((asset) => { var _a, _b, _c; return !((_c = (_b = (_a = asset.data) === null || _a === void 0 ? void 0 : _a.info) === null || _b === void 0 ? void 0 : _b.asset) === null || _c === void 0 ? void 0 : _c.hidden); }));
    useEffect(() => {
        if (folderName && !folderIdForShopper && authProvider) {
            // We're accessing another shopper's uploads so we need to be able to upload to that shopper
            (() => __awaiter(void 0, void 0, void 0, function* () {
                const authToken = (yield authProvider.getAuthHeaders()).Authorization.split(' ')[1];
                const folderId = yield fetchFolderIdForShopper({
                    shopperId: folderName,
                    tenant: writeTenant,
                    authToken,
                });
                setFolderIdForShopper(folderId);
            }))();
        }
    }, [folderName, folderIdForShopper, writeTenant, authProvider]);
    const addEventListener = useCallback((type, event) => {
        if (!assetStore) {
            throw new Error('AssetStore not available');
        }
        assetStore.addEventListener(type, event);
    }, [assetStore]);
    const removeEventListener = useCallback((type, event) => {
        if (!assetStore) {
            throw new Error('AssetStore not available');
        }
        assetStore.removeEventListener(type, event);
    }, [assetStore]);
    const updateVisibleAssets = useCallback(() => {
        if (assetStore === null || assetStore === void 0 ? void 0 : assetStore.assets) {
            const validAssets = assetStore.assets.filter(showValidAssets);
            setVisibleAssets(validAssets);
        }
    }, [assetStore === null || assetStore === void 0 ? void 0 : assetStore.assets]);
    const refreshAssets = useCallback(() => {
        if (uploadedAssets.status !== 'ready') {
            return;
        }
        uploadedAssets.refreshAssets();
    }, [uploadedAssets]);
    useEffect(() => {
        if (uploadedAssets.status !== 'ready') {
            return;
        }
        updateVisibleAssets();
    }, [uploadedAssets.status, updateVisibleAssets]);
    const assetDeleted = useCallback((asset) => {
        onAssetDeleted === null || onAssetDeleted === void 0 ? void 0 : onAssetDeleted(asset);
        updateVisibleAssets();
    }, [updateVisibleAssets, onAssetDeleted]);
    const value = useMemo(() => ({
        assetStore,
        hasAssets,
        assetsExpireOn,
        onUpload,
        onUploadCompleted,
        assetDeleted,
        onError,
        fetchingStatus: uploadedAssets.status,
        canFetchMore: uploadedAssets.status === 'ready' || uploadedAssets.status === 'loading-more'
            ? uploadedAssets.canFetchMore
            : undefined,
        fetchMore: uploadedAssets.status === 'ready' ? uploadedAssets.fetchMore : undefined,
        folderIdForShopper,
        folderName,
        addEventListener,
        removeEventListener,
        visibleAssets,
        visibleLogoAssets,
        refreshAssets,
        updateVisibleAssets,
        uploadingAnnouncement,
        setUploadingAnnouncement,
    }), [
        hasAssets,
        assetsExpireOn,
        onUpload,
        onError,
        uploadedAssets,
        onUploadCompleted,
        assetDeleted,
        folderIdForShopper,
        folderName,
        addEventListener,
        removeEventListener,
        assetStore,
        visibleAssets,
        visibleLogoAssets,
        refreshAssets,
        updateVisibleAssets,
        uploadingAnnouncement,
        setUploadingAnnouncement,
    ]);
    return _jsx(UploadManagerContext.Provider, Object.assign({ value: value }, { children: children }));
});
UploadManagerProvider.displayName = 'UploadManagerProvider';
//# sourceMappingURL=UploadManagerProvider.js.map